import { useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@nextui-org/react';
import { markAnnouncementSeen } from 'common/src/api/users';
import useUserStore from 'common/src/stores/useUserStore';
import { useLanguageStore } from 'common/src/stores/useLanguageStore';
import { pendingAnnouncements, visibleSidebarKeys } from './announcements';
import type { SidebarItem } from '../components/layouts/sidebars/SidebarTypes';

// Las que ya se cerraron en esta visita: si el perfil guardado aún no se
// actualizó, no vuelven a aparecer al cambiar de página.
const handledThisVisit = new Set<string>();

/**
 * Novedades de la plataforma, una a la vez. Solo las de partes de la app que
 * el usuario ve en su menú (ver announcements.ts).
 */
export default function AnnouncementModal({
  sidebarItems,
}: {
  sidebarItems: SidebarItem[];
}) {
  const [, navigate] = useLocation();
  const { userProfile, setUserProfile, loadingProfile } = useUserStore();
  const language = useLanguageStore((state) => state.language);
  const [handled, setHandled] = useState(() => new Set(handledThisVisit));
  const [step, setStep] = useState({ id: '', index: 0 });

  const announcement = useMemo(
    () =>
      userProfile && !loadingProfile
        ? (pendingAnnouncements(
            userProfile,
            visibleSidebarKeys(sidebarItems, userProfile)
          ).find((a) => !handled.has(a.id)) ?? null)
        : null,
    [userProfile, loadingProfile, sidebarItems, handled]
  );

  if (!announcement || !userProfile) return null;

  const text = (copy: { es: string; en: string }) =>
    language === 'es' ? copy.es : copy.en;
  const index = step.id === announcement.id ? step.index : 0;
  const current = announcement.steps[index];
  const isLast = index === announcement.steps.length - 1;

  // Abrir o cerrar cuentan como vista. Se guarda sin esperar: si falla, la
  // próxima visita la vuelve a mostrar.
  const finish = (open: boolean) => {
    handledThisVisit.add(announcement.id);
    setHandled(new Set(handledThisVisit));
    setUserProfile({
      ...userProfile,
      seen_announcements: [
        ...(userProfile.seen_announcements ?? []),
        announcement.id,
      ],
    });
    markAnnouncementSeen(announcement.id).catch(() => undefined);
    if (open) navigate(announcement.route);
  };

  return (
    <Modal
      isOpen
      onClose={() => finish(false)}
      placement='center'
      size={announcement.steps.some((s) => s.image) ? '2xl' : 'md'}
      scrollBehavior='inside'
    >
      <ModalContent>
        <ModalHeader className='flex flex-col items-start gap-2'>
          <Chip size='sm' variant='flat'>
            {language === 'es' ? 'Novedad' : 'New'}
          </Chip>
          {text(current.title)}
        </ModalHeader>
        <ModalBody>
          {current.image && (
            <img
              key={current.image}
              src={current.image}
              alt={text(current.title)}
              className='w-full rounded-medium border border-default-200'
            />
          )}
          <p className='text-sm text-default-500'>{text(current.body)}</p>
        </ModalBody>
        <ModalFooter className='items-center justify-between'>
          <div className='flex gap-1.5'>
            {announcement.steps.length > 1 &&
              announcement.steps.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 w-1.5 rounded-full ${
                    i === index ? 'bg-primary' : 'bg-default-300'
                  }`}
                />
              ))}
          </div>
          <div className='flex gap-2'>
            <Button variant='light' onPress={() => finish(false)}>
              {language === 'es' ? 'Cerrar' : 'Close'}
            </Button>
            <Button
              color='primary'
              onPress={() =>
                isLast
                  ? finish(true)
                  : setStep({ id: announcement.id, index: index + 1 })
              }
            >
              {isLast
                ? text(announcement.cta)
                : language === 'es'
                  ? 'Siguiente'
                  : 'Next'}
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
